import { useCallback, useEffect } from 'react';

import type { QueryResult } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client';

import type { TermsOfServiceVersionResponse } from 'AuthenticatedRoutesContainer/fragments';
import type { AcceptTermsResponse, AcceptTermsVariables } from 'AuthenticatedRoutesContainer/mutations';
import { AcceptTermsMutation } from 'AuthenticatedRoutesContainer/mutations';
import type { AuthenticatedRoutesResponse } from 'AuthenticatedRoutesContainer/queries';
import { AuthenticatedRoutesQuery } from 'AuthenticatedRoutesContainer/queries';
import { EMPTY_TERMS } from 'AuthenticatedRoutesContainer/TermsOfServiceAcceptanceDialog/TermsOfServiceAcceptanceDialog';
import useHeartbeat from 'common/components/hooks/useHeartbeat/useHeartbeat';
import type { LoginResponse } from 'common/graphql/mutations/authentication';
import { LoginMutation } from 'common/graphql/mutations/authentication';
import { TrackedError } from 'ErrorBoundary/Error';

import { useMotd } from './Motd/useMotd';

export type UseAuthenticatedRoutesContainerInput = {
  shouldLogin: boolean;
};

export type UseAuthenticatedRoutesContainerOutput = {
  queryResult: QueryResult<AuthenticatedRoutesResponse>;
  shouldAcceptTerms: boolean;
  tosVersion: TermsOfServiceVersionResponse;
  onAcceptTOS: (version: string) => Promise<void>;
  showMotd: boolean;
  motd?: string | null;
  acknowledgeMotd: () => void;
};

export default function useAuthenticatedRoutesContainer({
  shouldLogin,
}: UseAuthenticatedRoutesContainerInput): UseAuthenticatedRoutesContainerOutput {
  useHeartbeat();

  const queryResult = useQuery<AuthenticatedRoutesResponse>(AuthenticatedRoutesQuery);

  const [loginMutation] = useMutation<LoginResponse>(LoginMutation);

  const [acceptTermsMutation] = useMutation<AcceptTermsResponse, AcceptTermsVariables>(AcceptTermsMutation, {
    refetchQueries: ['AuthenticatedRoutesQuery'],
  });

  const { data } = queryResult;

  const { showMotd, acknowledgeMotd } = useMotd(queryResult);

  const tosVersion = data?.tosVersions.length ? data?.tosVersions[0] : EMPTY_TERMS;

  const shouldAcceptTerms = Boolean(data?.me.shouldAcceptTos);

  /**
   * If shouldLogin prop is provided
   * Reset count of accepted login banners & fire LoginMutation
   * This happens only at the time when new JWT is written to the localStorage
   * @return {Promise<void>}
   */
  useEffect(() => {
    if (shouldLogin) {
      try {
        loginMutation();
      } catch (error) {
        /**
         * This is fire-and-forget mutation so error can be ignored for now
         * Later we can add proper logging
         */
      }
    }
  }, [shouldLogin, loginMutation]);

  const onAcceptTOS = useCallback(
    async (version: string) => {
      try {
        await acceptTermsMutation({
          variables: { tosVersion: version },
        });
      } catch (e) {
        throw new TrackedError({
          source: e,
          content: 'An error occurred while accepting Terms of Service.',
        });
      }
    },
    [acceptTermsMutation]
  );

  return {
    queryResult,
    shouldAcceptTerms,
    tosVersion,
    onAcceptTOS,
    showMotd,
    motd: data?.me.motd,
    acknowledgeMotd,
  };
}
